import React, { useState, useEffect, useRef } from "react";
import {
  X,
  Search,
  Calendar,
  Tag,
  Clock,
  User,
  Eye,
  EyeOff,
  ChevronDown,
} from "lucide-react";
import { useUser } from "../contexts/UserContext";
import Avatar from "./Avatar";
import DateRangePicker from "./DateRangePicker";

const FilterPanel = ({ isOpen, onClose, cards, onFilterChange, project }) => {
  const { users, user } = useUser();

  // Get project members (only users who are actual members of the project)
  const projectMembers = project?.members
    ? project.members
        .map((member) => {
          const userId =
            typeof member === "object" && member.user
              ? member.user._id || member.user.id || member.user
              : member;
          return users.find((u) => u._id === userId || u.id === userId);
        })
        .filter(Boolean)
    : [];
  const [keyword, setKeyword] = useState("");
  const [memberFilters, setMemberFilters] = useState({
    noMembers: false,
    assignedToMe: false,
    selectedMembers: [],
  });
  const [dueDateFilters, setDueDateFilters] = useState({
    noDates: false,
    overdue: false,
    dueInNextDay: false,
    dueInNextWeek: false,
    dueInNextMonth: false,
  });
  const [labelFilters, setLabelFilters] = useState([]);
  const [noLabels, setNoLabels] = useState(false);
  const [readStatusFilters, setReadStatusFilters] = useState({
    read: false,
    unread: false,
  });
  const [createdByMe, setCreatedByMe] = useState(false);
  const [createdByUsers, setCreatedByUsers] = useState([]);
  const [createdRangeStart, setCreatedRangeStart] = useState(null);
  const [createdRangeEnd, setCreatedRangeEnd] = useState(null);
  const [showCreatedRangePicker, setShowCreatedRangePicker] = useState(false);
  const [showMembersDropdown, setShowMembersDropdown] = useState(false);
  const [showLabelsDropdown, setShowLabelsDropdown] = useState(false);
  const [showCreatedByDropdown, setShowCreatedByDropdown] = useState(false);
  const [memberSearchTerm, setMemberSearchTerm] = useState("");
  const [labelSearchTerm, setLabelSearchTerm] = useState("");
  const [createdBySearchTerm, setCreatedBySearchTerm] = useState("");
  const [filteredCount, setFilteredCount] = useState(cards.length);
  const modalRef = useRef(null);
  const membersDropdownRef = useRef(null);
  const createdByDropdownRef = useRef(null);
  const labelsDropdownRef = useRef(null);
  const createdRangePickerRef = useRef(null);

  // Get all unique labels from cards with their colors
  const getAllLabels = () => {
    const labelMap = new Map();
    cards.forEach((card) => {
      if (card.labels) {
        card.labels.forEach((label) => {
          if (!labelMap.has(label.name)) {
            labelMap.set(label.name, {
              name: label.name,
              color: label.color || "green",
            });
          }
        });
      }
    });
    return Array.from(labelMap.values());
  };

  const allLabels = getAllLabels();

  const filteredProjectMembers = projectMembers.filter((member) => {
    const term = memberSearchTerm.trim().toLowerCase();
    if (!term) return true;
    return [member.name, member.email]
      .filter(Boolean)
      .some((value) => value.toLowerCase().includes(term));
  });

  const filteredLabels = allLabels.filter((label) => {
    const term = labelSearchTerm.trim().toLowerCase();
    if (!term) return true;
    return label.name?.toLowerCase().includes(term);
  });

  // Available creators: project members + any user who created a card in this board
  const availableCreators = React.useMemo(() => {
    const creatorMap = new Map();
    projectMembers.forEach((m) => {
      const id = m._id || m.id;
      if (id) creatorMap.set(id.toString(), m);
    });

    cards.forEach((card) => {
      if (card.createdBy) {
        if (
          typeof card.createdBy === "object" &&
          (card.createdBy._id || card.createdBy.id)
        ) {
          const id = (card.createdBy._id || card.createdBy.id).toString();
          if (!creatorMap.has(id)) {
            creatorMap.set(id, card.createdBy);
          }
        } else if (typeof card.createdBy === "string") {
          const found = users.find(
            (u) => (u._id || u.id)?.toString() === card.createdBy
          );
          if (found && !creatorMap.has(card.createdBy)) {
            creatorMap.set(card.createdBy, found);
          }
        }
      }
    });

    return Array.from(creatorMap.values());
  }, [projectMembers, cards, users]);

  const filteredCreatedByUsers = availableCreators.filter((member) => {
    const term = createdBySearchTerm.trim().toLowerCase();
    if (!term) return true;
    return [member.name, member.email]
      .filter(Boolean)
      .some((value) => value.toLowerCase().includes(term));
  });

  const selectedMemberObjects = projectMembers.filter((u) =>
    memberFilters.selectedMembers.includes(u._id || u.id)
  );

  const selectedCreatedByObjects = availableCreators.filter((u) =>
    createdByUsers.some((id) => (id?._id || id?.id || id)?.toString() === (u._id || u.id)?.toString())
  );

  const selectedLabelObjects = allLabels.filter((l) =>
    labelFilters.includes(l.name)
  );

  // Label colors - matching other components
  const labelColors = [
    {
      value: "light-green",
      bg: "bg-green-300",
      text: "text-black",
    },
    { value: "green", bg: "bg-green-500", text: "text-white" },
    {
      value: "dark-green",
      bg: "bg-green-700",
      text: "text-white",
    },
    {
      value: "light-yellow",
      bg: "bg-yellow-300",
      text: "text-black",
    },
    { value: "yellow", bg: "bg-yellow-500", text: "text-black" },
    {
      value: "dark-yellow",
      bg: "bg-yellow-700",
      text: "text-white",
    },
    { value: "orange", bg: "bg-orange-500", text: "text-white" },
    { value: "red", bg: "bg-red-500", text: "text-white" },
    { value: "purple", bg: "bg-purple-500", text: "text-white" },
    { value: "pink", bg: "bg-pink-500", text: "text-white" },
    { value: "blue", bg: "bg-blue-500", text: "text-white" },
    { value: "gray", bg: "bg-gray-500", text: "text-white" },
  ];

  // Get label color config
  const getLabelColorConfig = (labelColor) => {
    let colorConfig = labelColors.find((c) => c.value === labelColor);

    // Map light colors to their saturated equivalents for consistency
    if (!colorConfig) {
      colorConfig = labelColors.find((c) => c.value === "green");
    } else if (labelColor === "light-green") {
      colorConfig = labelColors.find((c) => c.value === "green");
    } else if (labelColor === "light-yellow") {
      colorConfig = labelColors.find((c) => c.value === "yellow");
    }

    return colorConfig || labelColors.find((c) => c.value === "green");
  };

  // Close modal or dropdowns on outside click
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        onClose();
      }

      if (
        membersDropdownRef.current &&
        !membersDropdownRef.current.contains(event.target)
      ) {
        setShowMembersDropdown(false);
      }

      if (
        createdByDropdownRef.current &&
        !createdByDropdownRef.current.contains(event.target)
      ) {
        setShowCreatedByDropdown(false);
      }

      if (
        labelsDropdownRef.current &&
        !labelsDropdownRef.current.contains(event.target)
      ) {
        setShowLabelsDropdown(false);
      }

      if (
        createdRangePickerRef.current &&
        !createdRangePickerRef.current.contains(event.target)
      ) {
        setShowCreatedRangePicker(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, onClose]);

  // Apply filters whenever filter states change (debounced so typing in the
  // keyword box doesn't re-scan every card on each keystroke).
  useEffect(() => {
    const timer = setTimeout(() => {
      applyFilters();
    }, 200);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    keyword,
    memberFilters,
    dueDateFilters,
    labelFilters,
    noLabels,
    readStatusFilters,
    createdByMe,
    createdByUsers,
    createdRangeStart,
    createdRangeEnd,
  ]);

  const applyFilters = () => {
    let filteredCards = [...cards];

    // Keyword filter
    if (keyword.trim()) {
      const searchTerm = keyword.toLowerCase();
      const searchTermTrimmed = keyword.trim();
      const searchTermWithoutHash = searchTermTrimmed.replace(/^#/, "");

      filteredCards = filteredCards.filter((card) => {
        // Check if query matches card number (handle "#27" or "27")
        const cardNumberStr = card.cardNumber?.toString() || "";
        const cardNumberMatch =
          cardNumberStr === searchTermWithoutHash ||
          cardNumberStr.includes(searchTermWithoutHash) ||
          `#${cardNumberStr}`.toLowerCase().includes(searchTerm);

        const titleMatch = card.title?.toLowerCase().includes(searchTerm);
        const descriptionMatch = card.description
          ?.toLowerCase()
          .includes(searchTerm);

        return cardNumberMatch || titleMatch || descriptionMatch;
      });
    }

    // Member filters
    if (memberFilters.noMembers && memberFilters.selectedMembers.length > 0) {
      filteredCards = filteredCards.filter(
        (card) =>
          !card.assignees ||
          card.assignees.length === 0 ||
          card.assignees.some((assignee) => {
            const userId =
              typeof assignee === "object" ? assignee._id : assignee;
            return memberFilters.selectedMembers.includes(userId);
          })
      );
    } else if (memberFilters.noMembers) {
      filteredCards = filteredCards.filter(
        (card) => !card.assignees || card.assignees.length === 0
      );
    } else if (memberFilters.selectedMembers.length > 0) {
      filteredCards = filteredCards.filter((card) =>
        card.assignees?.some((assignee) => {
          const userId =
            typeof assignee === "object" ? assignee._id : assignee;
          return memberFilters.selectedMembers.includes(userId);
        })
      );
    }

    // Assigned to me filter
    if (memberFilters.assignedToMe && user) {
      filteredCards = filteredCards.filter((card) =>
        card.assignees?.some((assignee) => {
          const userId =
            typeof assignee === "object" ? assignee._id : assignee;
          return userId === user?._id || userId === user?.id;
        })
      );
    }

    // Due date filters (Multiple checkboxes can be selected)
    const hasDueDateFilters =
      dueDateFilters.noDates ||
      dueDateFilters.overdue ||
      dueDateFilters.dueInNextDay ||
      dueDateFilters.dueInNextWeek ||
      dueDateFilters.dueInNextMonth;

    if (hasDueDateFilters) {
      const now = new Date();
      const tomorrow = new Date(now);
      tomorrow.setDate(tomorrow.getDate() + 1);
      const nextWeek = new Date(now);
      nextWeek.setDate(nextWeek.getDate() + 7);
      const nextMonth = new Date(now);
      nextMonth.setMonth(nextMonth.getMonth() + 1);

      filteredCards = filteredCards.filter((card) => {
        // If card has no due date and "No dates" is checked
        if (!card.dueDate && dueDateFilters.noDates) {
          return true;
        }

        // If card has a due date, check other filters
        if (card.dueDate) {
          const dueDate = new Date(card.dueDate);

          // Overdue
          if (dueDateFilters.overdue && dueDate < now) {
            return true;
          }

          // Due in next day
          if (
            dueDateFilters.dueInNextDay &&
            dueDate >= now &&
            dueDate <= tomorrow
          ) {
            return true;
          }

          // Due in next week
          if (
            dueDateFilters.dueInNextWeek &&
            dueDate >= now &&
            dueDate <= nextWeek
          ) {
            return true;
          }

          // Due in next month
          if (
            dueDateFilters.dueInNextMonth &&
            dueDate >= now &&
            dueDate <= nextMonth
          ) {
            return true;
          }
        }

        return false;
      });
    }

    // Created by me filter
    if (createdByMe && user) {
      filteredCards = filteredCards.filter((card) => {
        const creatorId =
          typeof card.createdBy === "object"
            ? card.createdBy?._id || card.createdBy?.id
            : card.createdBy;
        const currentUserId = user?._id || user?.id;
        return (
          creatorId &&
          currentUserId &&
          creatorId.toString() === currentUserId.toString()
        );
      });
    }

    // Created by specific users filter
    if (createdByUsers.length > 0) {
      filteredCards = filteredCards.filter((card) => {
        const creatorId =
          typeof card.createdBy === "object"
            ? card.createdBy?._id || card.createdBy?.id
            : card.createdBy;
        if (!creatorId) return false;
        return createdByUsers.some(
          (uId) =>
            (uId?._id || uId?.id || uId)?.toString() === creatorId.toString()
        );
      });
    }

    // Created date custom range
    if (createdRangeStart || createdRangeEnd) {
      filteredCards = filteredCards.filter((card) => {
        if (!card.createdAt) return false;
        const created = new Date(card.createdAt);
        if (createdRangeStart) {
          const start = new Date(createdRangeStart);
          start.setHours(0, 0, 0, 0);
          if (created < start) return false;
        }
        if (createdRangeEnd) {
          const end = new Date(createdRangeEnd);
          end.setHours(23, 59, 59, 999);
          if (created > end) return false;
        }
        return true;
      });
    }

    // Label filters
    if (noLabels && labelFilters.length > 0) {
      filteredCards = filteredCards.filter(
        (card) =>
          !card.labels ||
          card.labels.length === 0 ||
          card.labels.some((label) => labelFilters.includes(label.name))
      );
    } else if (noLabels) {
      filteredCards = filteredCards.filter(
        (card) => !card.labels || card.labels.length === 0
      );
    } else if (labelFilters.length > 0) {
      filteredCards = filteredCards.filter((card) =>
        card.labels?.some((label) => labelFilters.includes(label.name))
      );
    }

    // Read/Unread filter (checking both, or neither, shows all cards)
    if (
      user &&
      readStatusFilters.read !== readStatusFilters.unread
    ) {
      filteredCards = filteredCards.filter((card) => {
        // Check if card is read by current user
        const isRead = card.readBy?.some(
          (readEntry) =>
            (readEntry.user?._id || readEntry.user) === (user._id || user.id)
        );

        if (readStatusFilters.read) {
          return isRead;
        }
        return !isRead;
      });
    }

    setFilteredCount(filteredCards.length);
    onFilterChange(filteredCards, hasActiveFilters());
  };

  const handleMemberToggle = (memberId) => {
    setMemberFilters((prev) => ({
      ...prev,
      selectedMembers: prev.selectedMembers.includes(memberId)
        ? prev.selectedMembers.filter((id) => id !== memberId)
        : [...prev.selectedMembers, memberId],
    }));
  };

  const handleCreatedByToggle = (userId) => {
    setCreatedByUsers((prev) =>
      prev.includes(userId)
        ? prev.filter((id) => id !== userId)
        : [...prev, userId]
    );
  };

  const handleLabelToggle = (labelName) => {
    setLabelFilters((prev) =>
      prev.includes(labelName)
        ? prev.filter((name) => name !== labelName)
        : [...prev, labelName]
    );
  };

  const clearAllFilters = () => {
    setKeyword("");
    setMemberFilters({
      noMembers: false,
      assignedToMe: false,
      selectedMembers: [],
    });
    setDueDateFilters({
      noDates: false,
      overdue: false,
      dueInNextDay: false,
      dueInNextWeek: false,
      dueInNextMonth: false,
    });
    setLabelFilters([]);
    setNoLabels(false);
    setReadStatusFilters({ read: false, unread: false });
    setCreatedByMe(false);
    setCreatedByUsers([]);
    setCreatedRangeStart(null);
    setCreatedRangeEnd(null);
    setMemberSearchTerm("");
    setLabelSearchTerm("");
    setCreatedBySearchTerm("");
  };

  const hasActiveFilters = () => {
    return (
      keyword.trim() !== "" ||
      memberFilters.noMembers ||
      memberFilters.assignedToMe ||
      memberFilters.selectedMembers.length > 0 ||
      dueDateFilters.noDates ||
      dueDateFilters.overdue ||
      dueDateFilters.dueInNextDay ||
      dueDateFilters.dueInNextWeek ||
      dueDateFilters.dueInNextMonth ||
      labelFilters.length > 0 ||
      noLabels ||
      readStatusFilters.read ||
      readStatusFilters.unread ||
      createdByMe ||
      createdByUsers.length > 0 ||
      Boolean(createdRangeStart) ||
      Boolean(createdRangeEnd)
    );
  };

  // Calculate counts for each filter option
  const getNoLabelsCount = () => {
    return cards.filter(
      (card) => !card.labels || card.labels.length === 0
    ).length;
  };

  const getNoMembersCount = () => {
    return cards.filter(
      (card) => !card.assignees || card.assignees.length === 0
    ).length;
  };

  const getAssignedToMeCount = () => {
    return cards.filter((card) =>
      card.assignees?.some((assignee) => {
        const userId = typeof assignee === "object" ? assignee._id : assignee;
        return userId === user?._id || userId === user?.id;
      })
    ).length;
  };

  const getCreatedByMeCount = () => {
    return cards.filter((card) => {
      const creatorId =
        typeof card.createdBy === "object"
          ? card.createdBy?._id || card.createdBy?.id
          : card.createdBy;
      const currentUserId = user?._id || user?.id;
      return (
        creatorId &&
        currentUserId &&
        creatorId.toString() === currentUserId.toString()
      );
    }).length;
  };

  const getCreatedByCount = (userId) => {
    return cards.filter((card) => {
      const creatorId =
        typeof card.createdBy === "object"
          ? card.createdBy?._id || card.createdBy?.id
          : card.createdBy;
      return (
        creatorId &&
        userId &&
        creatorId.toString() === (userId?._id || userId?.id || userId).toString()
      );
    }).length;
  };

  const getMemberCount = (memberId) => {
    return cards.filter((card) =>
      card.assignees?.some((assignee) => {
        const userId = typeof assignee === "object" ? assignee._id : assignee;
        return userId === memberId;
      })
    ).length;
  };

  const getOverdueCount = () => {
    const now = new Date();
    return cards.filter((card) => card.dueDate && new Date(card.dueDate) < now)
      .length;
  };

  const getDueInNextDayCount = () => {
    const now = new Date();
    const tomorrow = new Date(now);
    tomorrow.setDate(tomorrow.getDate() + 1);
    return cards.filter(
      (card) =>
        card.dueDate &&
        new Date(card.dueDate) >= now &&
        new Date(card.dueDate) <= tomorrow
    ).length;
  };

  const getDueInNextWeekCount = () => {
    const now = new Date();
    const nextWeek = new Date(now);
    nextWeek.setDate(nextWeek.getDate() + 7);
    return cards.filter(
      (card) =>
        card.dueDate &&
        new Date(card.dueDate) >= now &&
        new Date(card.dueDate) <= nextWeek
    ).length;
  };

  const getDueInNextMonthCount = () => {
    const now = new Date();
    const nextMonth = new Date(now);
    nextMonth.setMonth(nextMonth.getMonth() + 1);
    return cards.filter(
      (card) =>
        card.dueDate &&
        new Date(card.dueDate) >= now &&
        new Date(card.dueDate) <= nextMonth
    ).length;
  };

  const getNoDatesCount = () => {
    return cards.filter((card) => !card.dueDate).length;
  };

  const getLabelCount = (labelName) => {
    return cards.filter((card) =>
      card.labels?.some((label) => label.name === labelName)
    ).length;
  };

  const formatRangeDate = (isoDate) => {
    if (!isoDate) return null;
    return new Date(isoDate).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  // Get read/unread counts
  const getReadCount = () => {
    if (!user) return 0;
    return cards.filter((card) => {
      return card.readBy?.some(
        (readEntry) =>
          (readEntry.user?._id || readEntry.user) === (user._id || user.id)
      );
    }).length;
  };

  const getUnreadCount = () => {
    if (!user) return cards.length;
    return cards.filter((card) => {
      return !card.readBy?.some(
        (readEntry) =>
          (readEntry.user?._id || readEntry.user) === (user._id || user.id)
      );
    }).length;
  };

  return (
    <>
      {/* Filter Modal */}
      {isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 dark:bg-opacity-70 flex items-center justify-center z-50">
          <div
            ref={modalRef}
            className="bg-white dark:bg-slate-800 rounded-lg shadow-2xl w-full max-w-4xl mx-4 max-h-[96vh] flex flex-col transition-colors duration-200"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-6 h-[61.19px] min-h-[61.19px] max-h-[61.19px] bg-[#F1F2F4] dark:bg-slate-900 border-b border-gray-200 dark:border-slate-700 shrink-0 box-border rounded-t-lg transition-colors duration-200">
              <h2 className="text-[20px] font-semibold tracking-tight text-gray-700 dark:text-white">Filter</h2>
              <button
                onClick={onClose}
                className="w-8 h-8 min-w-[32px] min-h-[32px] max-w-[32px] max-h-[32px] flex items-center justify-center rounded-lg hover:bg-gray-400/20 transition-colors text-gray-600 dark:text-slate-400"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Body: single scrolling column */}
            <div className="flex-1 min-h-0 overflow-y-auto p-4 space-y-6">
              {/* Keyword */}
              <div>
                <h3 className="text-sm font-semibold text-gray-700 dark:text-slate-200 mb-2">
                  Keyword
                </h3>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-slate-500" />
                  <input
                    type="text"
                    value={keyword}
                    onChange={(e) => setKeyword(e.target.value)}
                    placeholder="Enter a keyword..."
                    className="w-full h-8 pl-10 pr-3 border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-black dark:text-white rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-transparent text-xs transition-colors duration-200"
                  />
                </div>
                <p className="text-xs text-gray-500 dark:text-slate-500 mt-2">
                  Search cards (#27), members, labels, and more.
                </p>
              </div>
              {/* divide as a grid to two column and also allign center*/}
              <div className="grid grid-cols-2 gap-4 ">
                {/* Members */}
                <div className="col-span-2 md:col-span-1">
                  <h3 className="text-sm font-semibold text-gray-700 dark:text-slate-200 mb-2">
                    Assignees
                  </h3>
                  <div className="space-y-1">
                    <label className="flex items-center h-8 space-x-2 cursor-pointer group px-2 rounded-lg hover:bg-gray-50 dark:hover:bg-slate-700">
                      <input
                        type="checkbox"
                        checked={memberFilters.assignedToMe}
                        onChange={(e) =>
                          setMemberFilters((prev) => ({
                            ...prev,
                            assignedToMe: e.target.checked,
                          }))
                        }
                        className="w-4 h-4 text-blue-600 border-gray-300 dark:border-slate-600 rounded focus:ring-blue-500 bg-white dark:bg-slate-900"
                      />
                      <div className="flex items-center space-x-2 flex-1">
                        <Avatar user={user} size="xs" />
                        <span className="text-sm text-gray-700 dark:text-slate-300 group-hover:text-gray-900 dark:group-hover:text-white transition-colors">
                          Cards assigned to me
                        </span>
                      </div>
                      {memberFilters.assignedToMe && (
                        <span className="text-xs text-gray-500 dark:text-slate-500">
                          ({getAssignedToMeCount()})
                        </span>
                      )}
                    </label>
                  </div>

                  <div className="relative mt-2" ref={membersDropdownRef}>
                    <button
                      type="button"
                      onClick={() => {
                        setShowMembersDropdown((prev) => !prev);
                        setShowCreatedByDropdown(false);
                        setShowLabelsDropdown(false);
                      }}
                      className="w-full h-8 flex items-center justify-between gap-2 px-3 border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-900 rounded-lg hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors text-xs"
                    >
                      <div className="flex flex-wrap items-center gap-1 min-w-0">
                        {memberFilters.noMembers && (
                          <span className="flex items-center gap-1 pl-1.5 pr-2 py-0.5 rounded-full bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 text-xs">
                            <User className="w-3 h-3 text-blue-500" />
                            No Assignees
                          </span>
                        )}
                        {selectedMemberObjects.map((u) => (
                          <span
                            key={u._id || u.id}
                            className="flex items-center gap-1 pl-1 pr-2 py-0.5 rounded-full bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 text-xs"
                          >
                            <Avatar user={u} size="xs" />
                            {u.name}
                          </span>
                        ))}
                        {!memberFilters.noMembers && selectedMemberObjects.length === 0 && (
                          <span className="text-gray-500 dark:text-slate-400">
                            Select members
                          </span>
                        )}
                      </div>
                      <ChevronDown
                        className={`w-4 h-4 text-gray-400 dark:text-slate-500 shrink-0 transition-transform ${
                          showMembersDropdown ? "rotate-180" : ""
                        }`}
                      />
                    </button>

                    {showMembersDropdown && (
                      <div className="absolute top-full left-0 right-0 z-50 mt-1.5 bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-700 rounded-xl p-2.5 shadow-2xl animate-in fade-in zoom-in-95 duration-150">
                        <div className="relative mb-2">
                          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-slate-500" />
                          <input
                            type="text"
                            value={memberSearchTerm}
                            onChange={(e) => setMemberSearchTerm(e.target.value)}
                            placeholder="Search members"
                            autoFocus
                            className="w-full h-8 pl-10 pr-3 border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-black dark:text-white rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-transparent text-xs transition-colors duration-200"
                          />
                        </div>
                        <div className="max-h-48 overflow-y-auto space-y-1">
                          {(!memberSearchTerm || "no assignees".includes(memberSearchTerm.toLowerCase())) && (
                            <label className="flex items-center space-x-2 px-2 py-1.5 rounded-lg hover:bg-gray-50 dark:hover:bg-slate-700 cursor-pointer transition-colors border-b border-gray-100 dark:border-slate-800 pb-2 mb-1">
                              <input
                                type="checkbox"
                                checked={memberFilters.noMembers}
                                onChange={(e) =>
                                  setMemberFilters((prev) => ({
                                    ...prev,
                                    noMembers: e.target.checked,
                                  }))
                                }
                                className="w-4 h-4 text-blue-600 border-gray-300 dark:border-slate-600 rounded focus:ring-blue-500 bg-white dark:bg-slate-900"
                              />
                              <User className="w-4 h-4 text-gray-400 dark:text-slate-500" />
                              <span className="text-sm text-gray-700 dark:text-slate-300 font-medium">
                                No Assignees
                              </span>
                              <span className="text-xs text-gray-500 dark:text-slate-500 ml-auto">
                                ({getNoMembersCount()})
                              </span>
                            </label>
                          )}
                          {filteredProjectMembers.length > 0 ? (
                            filteredProjectMembers.map((u) => (
                              <label
                                key={u._id || u.id}
                                className="flex items-center space-x-2 px-2 py-1.5 rounded-lg hover:bg-gray-50 dark:hover:bg-slate-700 cursor-pointer transition-colors"
                              >
                                <input
                                  type="checkbox"
                                  checked={memberFilters.selectedMembers.includes(
                                    u._id || u.id
                                  )}
                                  onChange={() =>
                                    handleMemberToggle(u._id || u.id)
                                  }
                                  className="w-4 h-4 text-blue-600 border-gray-300 dark:border-slate-600 rounded focus:ring-blue-500 bg-white dark:bg-slate-900"
                                />
                                <Avatar user={u} size="xs" />
                                <span className="text-sm text-gray-700 dark:text-slate-300">
                                  {u.name}
                                </span>
                                {memberFilters.selectedMembers.includes(
                                  u._id || u.id
                                ) && (
                                  <span className="text-xs text-gray-500 dark:text-slate-500 ml-auto">
                                    ({getMemberCount(u._id || u.id)})
                                  </span>
                                )}
                              </label>
                            ))
                          ) : (
                            <div className="px-2 py-2 text-sm text-gray-500 dark:text-slate-500">
                              {projectMembers.length === 0
                                ? "No project members found"
                                : "No matching members"}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Created by */}
                <div className="col-span-2 md:col-span-1">
                  <h3 className="text-sm font-semibold text-gray-700 dark:text-slate-200 mb-2">
                    Created by
                  </h3>
                  <div className="space-y-1">
                    <label className="flex items-center h-8 space-x-2 cursor-pointer group px-2 rounded-lg hover:bg-gray-50 dark:hover:bg-slate-700">
                      <input
                        type="checkbox"
                        checked={createdByMe}
                        onChange={(e) => setCreatedByMe(e.target.checked)}
                        className="w-4 h-4 text-blue-600 border-gray-300 dark:border-slate-600 rounded focus:ring-blue-500 bg-white dark:bg-slate-900"
                      />
                      <div className="flex items-center space-x-2 flex-1">
                        <Avatar user={user} size="xs" />
                        <span className="text-sm text-gray-700 dark:text-slate-300 group-hover:text-gray-900 dark:group-hover:text-white transition-colors">
                          Cards created by me
                        </span>
                      </div>
                      {createdByMe && (
                        <span className="text-xs text-gray-500 dark:text-slate-500">
                          ({getCreatedByMeCount()})
                        </span>
                      )}
                    </label>
                  </div>

                  <div className="relative mt-2" ref={createdByDropdownRef}>
                    <button
                      type="button"
                      onClick={() => {
                        setShowCreatedByDropdown((prev) => !prev);
                        setShowMembersDropdown(false);
                        setShowLabelsDropdown(false);
                      }}
                      className="w-full h-8 flex items-center justify-between gap-2 px-3 border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-900 rounded-lg hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors text-xs"
                    >
                      <div className="flex flex-wrap items-center gap-1 min-w-0">
                        {selectedCreatedByObjects.length > 0 ? (
                          selectedCreatedByObjects.map((u) => (
                            <span
                              key={u._id || u.id}
                              className="flex items-center gap-1 pl-1 pr-2 py-0.5 rounded-full bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 text-xs"
                            >
                              <Avatar user={u} size="xs" />
                              {u.name}
                            </span>
                          ))
                        ) : (
                          <span className="text-gray-500 dark:text-slate-400">
                            Select creator
                          </span>
                        )}
                      </div>
                      <ChevronDown
                        className={`w-4 h-4 text-gray-400 dark:text-slate-500 shrink-0 transition-transform ${
                          showCreatedByDropdown ? "rotate-180" : ""
                        }`}
                      />
                    </button>

                    {showCreatedByDropdown && (
                      <div className="absolute top-full left-0 right-0 z-50 mt-1.5 bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-700 rounded-xl p-2.5 shadow-2xl animate-in fade-in zoom-in-95 duration-150">
                        <div className="relative mb-2">
                          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-slate-500" />
                          <input
                            type="text"
                            value={createdBySearchTerm}
                            onChange={(e) =>
                              setCreatedBySearchTerm(e.target.value)
                            }
                            placeholder="Search creators"
                            autoFocus
                            className="w-full h-8 pl-10 pr-3 border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-black dark:text-white rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-transparent text-xs transition-colors duration-200"
                          />
                        </div>
                        <div className="max-h-48 overflow-y-auto space-y-1">
                          {filteredCreatedByUsers.length > 0 ? (
                            filteredCreatedByUsers.map((u) => (
                              <label
                                key={u._id || u.id}
                                className="flex items-center space-x-2 px-2 py-1.5 rounded-lg hover:bg-gray-50 dark:hover:bg-slate-700 cursor-pointer transition-colors"
                              >
                                <input
                                  type="checkbox"
                                  checked={createdByUsers.some((id) => (id?._id || id?.id || id)?.toString() === (u._id || u.id)?.toString())}
                                  onChange={() =>
                                    handleCreatedByToggle(u._id || u.id)
                                  }
                                  className="w-4 h-4 text-blue-600 border-gray-300 dark:border-slate-600 rounded focus:ring-blue-500 bg-white dark:bg-slate-900"
                                />
                                <Avatar user={u} size="xs" />
                                <span className="text-sm text-gray-700 dark:text-slate-300">
                                  {u.name}
                                </span>
                                {createdByUsers.some((id) => (id?._id || id?.id || id)?.toString() === (u._id || u.id)?.toString()) && (
                                  <span className="text-xs text-gray-500 dark:text-slate-500 ml-auto">
                                    ({getCreatedByCount(u._id || u.id)})
                                  </span>
                                )}
                              </label>
                            ))
                          ) : (
                            <div className="px-2 py-2 text-sm text-gray-500 dark:text-slate-500">
                              {availableCreators.length === 0
                                ? "No creators found"
                                : "No matching creators"}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              {/* Labels and Card created date */}
              <div className="grid grid-cols-2 gap-4">
                {/* Labels */}
                <div className="col-span-2 md:col-span-1">
                  <h3 className="text-sm font-semibold text-gray-700 dark:text-slate-200 mb-2">
                    Labels
                  </h3>

                  <div className="relative" ref={labelsDropdownRef}>
                    <button
                      type="button"
                      onClick={() => {
                        setShowLabelsDropdown((prev) => !prev);
                        setShowMembersDropdown(false);
                        setShowCreatedByDropdown(false);
                        setShowCreatedRangePicker(false);
                      }}
                      className="w-full h-8 flex items-center justify-between gap-2 px-3 border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-900 rounded-lg hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors text-xs"
                    >
                      <div className="flex flex-wrap items-center gap-1 min-w-0">
                        {noLabels && (
                          <span className="flex items-center gap-1 pl-1.5 pr-2 py-0.5 rounded-full bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 text-xs">
                            <Tag className="w-3 h-3 text-blue-500" />
                            No labels
                          </span>
                        )}
                        {selectedLabelObjects.map((label) => {
                          const colorConfig = getLabelColorConfig(label.color);
                          return (
                            <span
                              key={label.name}
                              className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${colorConfig.bg} ${colorConfig.text}`}
                            >
                              {label.name}
                            </span>
                          );
                        })}
                        {!noLabels && selectedLabelObjects.length === 0 && (
                          <span className="text-gray-500 dark:text-slate-400">
                            Select labels
                          </span>
                        )}
                      </div>
                      <ChevronDown
                        className={`w-4 h-4 text-gray-400 dark:text-slate-500 shrink-0 transition-transform ${
                          showLabelsDropdown ? "rotate-180" : ""
                        }`}
                      />
                    </button>

                    {showLabelsDropdown && (
                      <div className="absolute top-full left-0 right-0 z-50 mt-1.5 bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-700 rounded-xl p-2.5 shadow-2xl animate-in fade-in zoom-in-95 duration-150">
                        <div className="relative mb-2">
                          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-slate-500" />
                          <input
                            type="text"
                            value={labelSearchTerm}
                            onChange={(e) => setLabelSearchTerm(e.target.value)}
                            placeholder="Search labels"
                            autoFocus
                            className="w-full h-8 pl-10 pr-3 border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-black dark:text-white rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-transparent text-xs transition-colors duration-200"
                          />
                        </div>
                        <div className="max-h-48 overflow-y-auto space-y-1">
                          {(!labelSearchTerm || "no labels".includes(labelSearchTerm.toLowerCase())) && (
                            <label className="flex items-center space-x-2 px-2 py-1.5 rounded-lg hover:bg-gray-50 dark:hover:bg-slate-700 cursor-pointer transition-colors border-b border-gray-100 dark:border-slate-800 pb-2 mb-1">
                              <input
                                type="checkbox"
                                checked={noLabels}
                                onChange={(e) => setNoLabels(e.target.checked)}
                                className="w-4 h-4 text-blue-600 border-gray-300 dark:border-slate-600 rounded focus:ring-blue-500 bg-white dark:bg-slate-900"
                              />
                              <Tag className="w-4 h-4 text-gray-400 dark:text-slate-500" />
                              <span className="text-sm text-gray-700 dark:text-slate-300 font-medium">
                                No labels
                              </span>
                              <span className="text-xs text-gray-500 dark:text-slate-500 ml-auto">
                                ({getNoLabelsCount()})
                              </span>
                            </label>
                          )}
                          {filteredLabels.length > 0 ? (
                            filteredLabels.map((label) => {
                              const colorConfig = getLabelColorConfig(label.color);
                              return (
                                <label
                                  key={label.name}
                                  className="flex items-center space-x-2 px-2 py-1.5 rounded-lg hover:bg-gray-50 dark:hover:bg-slate-700 cursor-pointer transition-colors"
                                >
                                  <input
                                    type="checkbox"
                                    checked={labelFilters.includes(label.name)}
                                    onChange={() => handleLabelToggle(label.name)}
                                    className="w-4 h-4 text-blue-600 border-gray-300 dark:border-slate-600 rounded focus:ring-blue-500 bg-white dark:bg-slate-900"
                                  />
                                  <span
                                    className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${colorConfig.bg} ${colorConfig.text}`}
                                  >
                                    {label.name}
                                  </span>
                                  {labelFilters.includes(label.name) && (
                                    <span className="text-xs text-gray-500 dark:text-slate-500 ml-auto">
                                      ({getLabelCount(label.name)})
                                    </span>
                                  )}
                                </label>
                              );
                            })
                          ) : (
                            <div className="px-2 py-2 text-sm text-gray-500 dark:text-slate-500">
                              No matching labels
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Card created date */}
                <div className="col-span-2 md:col-span-1">
                  <h3 className="text-sm font-semibold text-gray-700 dark:text-slate-200 mb-2">
                    Card created date
                  </h3>
                  <div className="relative" ref={createdRangePickerRef}>
                    <button
                      type="button"
                      onClick={() => {
                        setShowCreatedRangePicker((prev) => !prev);
                        setShowMembersDropdown(false);
                        setShowCreatedByDropdown(false);
                        setShowLabelsDropdown(false);
                      }}
                      className={`w-full h-8 text-left flex items-center justify-between px-3 rounded-lg text-xs transition-colors border ${
                        createdRangeStart || createdRangeEnd
                          ? "border-blue-500 bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 font-medium"
                          : "border-gray-300 dark:border-slate-600 hover:bg-gray-50 dark:hover:bg-slate-700 text-gray-600 dark:text-slate-300 bg-white dark:bg-slate-900"
                      }`}
                    >
                      <span className="truncate">
                        {createdRangeStart || createdRangeEnd
                          ? `${formatRangeDate(createdRangeStart) || "Start"} - ${formatRangeDate(createdRangeEnd) || "End"}`
                          : "Custom date range..."}
                      </span>
                      {createdRangeStart || createdRangeEnd ? (
                        <span
                          onClick={(e) => {
                            e.stopPropagation();
                            setCreatedRangeStart(null);
                            setCreatedRangeEnd(null);
                          }}
                          className="hover:text-red-500 ml-1 p-0.5 rounded cursor-pointer"
                          title="Clear custom range"
                        >
                          <X className="w-3.5 h-3.5" />
                        </span>
                      ) : (
                        <Calendar className="w-3.5 h-3.5 text-gray-400 shrink-0 ml-1" />
                      )}
                    </button>

                    {showCreatedRangePicker && (
                      <DateRangePicker
                        initialStart={createdRangeStart}
                        initialEnd={createdRangeEnd}
                        startValue={createdRangeStart}
                        endValue={createdRangeEnd}
                        className="absolute right-0 top-full mt-1.5 z-50 flex bg-white dark:bg-slate-800 rounded-xl shadow-2xl border border-gray-200 dark:border-slate-700 overflow-hidden animate-in fade-in zoom-in-95 duration-150"
                        onChange={({ start, end }) => {
                          setCreatedRangeStart(start);
                          setCreatedRangeEnd(end);
                          setShowCreatedRangePicker(false);
                        }}
                        onClose={() => setShowCreatedRangePicker(false)}
                      />
                    )}
                  </div>
                </div>
              </div>

              {/* Due date & Read Status Grid Container */}
              <div className="grid grid-cols-2 gap-4">
                {/* Due date */}
                <div className="col-span-2 md:col-span-1">
                  <h3 className="text-sm font-semibold text-gray-700 dark:text-slate-200 mb-2">
                    Due date
                  </h3>
                  <div className="space-y-1">
                    <label className="flex items-center h-8 space-x-2 cursor-pointer group px-2 rounded-lg hover:bg-gray-50 dark:hover:bg-slate-700">
                      <input
                        type="checkbox"
                        checked={dueDateFilters.noDates}
                        onChange={(e) =>
                          setDueDateFilters((prev) => ({
                            ...prev,
                            noDates: e.target.checked,
                          }))
                        }
                        className="w-4 h-4 text-blue-600 border-gray-300 dark:border-slate-600 rounded focus:ring-blue-500 bg-white dark:bg-slate-900"
                      />
                      <Calendar className="w-4 h-4 text-gray-400 dark:text-slate-500" />
                      <span className="text-sm text-gray-700 dark:text-slate-300 group-hover:text-gray-900 dark:group-hover:text-white transition-colors">No dates</span>
                      {dueDateFilters.noDates && (
                        <span className="text-xs text-gray-500 dark:text-slate-500 ml-auto">
                          ({getNoDatesCount()})
                        </span>
                      )}
                    </label>
                    <label className="flex items-center h-8 space-x-2 cursor-pointer group px-2 rounded-lg hover:bg-gray-50 dark:hover:bg-slate-700">
                      <input
                        type="checkbox"
                        checked={dueDateFilters.overdue}
                        onChange={(e) =>
                          setDueDateFilters((prev) => ({
                            ...prev,
                            overdue: e.target.checked,
                          }))
                        }
                        className="w-4 h-4 text-blue-600 border-gray-300 dark:border-slate-600 rounded focus:ring-blue-500 bg-white dark:bg-slate-900"
                      />
                      <Clock className="w-4 h-4 text-red-500 dark:text-red-400" />
                      <span className="text-sm text-gray-700 dark:text-slate-300 group-hover:text-gray-900 dark:group-hover:text-white transition-colors">Overdue</span>
                      {dueDateFilters.overdue && (
                        <span className="text-xs text-gray-500 dark:text-slate-500 ml-auto">
                          ({getOverdueCount()})
                        </span>
                      )}
                    </label>
                    <label className="flex items-center h-8 space-x-2 cursor-pointer group px-2 rounded-lg hover:bg-gray-50 dark:hover:bg-slate-700">
                      <input
                        type="checkbox"
                        checked={dueDateFilters.dueInNextDay}
                        onChange={(e) =>
                          setDueDateFilters((prev) => ({
                            ...prev,
                            dueInNextDay: e.target.checked,
                          }))
                        }
                        className="w-4 h-4 text-blue-600 border-gray-300 dark:border-slate-600 rounded focus:ring-blue-500 bg-white dark:bg-slate-900"
                      />
                      <Clock className="w-4 h-4 text-yellow-500 dark:text-yellow-400" />
                      <span className="text-sm text-gray-700 dark:text-slate-300 group-hover:text-gray-900 dark:group-hover:text-white transition-colors">
                        Due in the next day
                      </span>
                      {dueDateFilters.dueInNextDay && (
                        <span className="text-xs text-gray-500 dark:text-slate-500 ml-auto">
                          ({getDueInNextDayCount()})
                        </span>
                      )}
                    </label>
                    <label className="flex items-center h-8 space-x-2 cursor-pointer group px-2 rounded-lg hover:bg-gray-50 dark:hover:bg-slate-700">
                      <input
                        type="checkbox"
                        checked={dueDateFilters.dueInNextWeek}
                        onChange={(e) =>
                          setDueDateFilters((prev) => ({
                            ...prev,
                            dueInNextWeek: e.target.checked,
                          }))
                        }
                        className="w-4 h-4 text-blue-600 border-gray-300 dark:border-slate-600 rounded focus:ring-blue-500 bg-white dark:bg-slate-900"
                      />
                      <Clock className="w-4 h-4 text-gray-400 dark:text-slate-500" />
                      <span className="text-sm text-gray-700 dark:text-slate-300 group-hover:text-gray-900 dark:group-hover:text-white transition-colors">
                        Due in the next week
                      </span>
                      {dueDateFilters.dueInNextWeek && (
                        <span className="text-xs text-gray-500 dark:text-slate-500 ml-auto">
                          ({getDueInNextWeekCount()})
                        </span>
                      )}
                    </label>
                    <label className="flex items-center h-8 space-x-2 cursor-pointer group px-2 rounded-lg hover:bg-gray-50 dark:hover:bg-slate-700">
                      <input
                        type="checkbox"
                        checked={dueDateFilters.dueInNextMonth}
                        onChange={(e) =>
                          setDueDateFilters((prev) => ({
                            ...prev,
                            dueInNextMonth: e.target.checked,
                          }))
                        }
                        className="w-4 h-4 text-blue-600 border-gray-300 dark:border-slate-600 rounded focus:ring-blue-500 bg-white dark:bg-slate-900"
                      />
                      <Clock className="w-4 h-4 text-gray-400 dark:text-slate-500" />
                      <span className="text-sm text-gray-700 dark:text-slate-300 group-hover:text-gray-900 dark:group-hover:text-white transition-colors">
                        Due in the next month
                      </span>
                      {dueDateFilters.dueInNextMonth && (
                        <span className="text-xs text-gray-500 dark:text-slate-500 ml-auto">
                          ({getDueInNextMonthCount()})
                        </span>
                      )}
                    </label>
                  </div>
                </div>

                {/* Read Status */}
                <div className="col-span-2 md:col-span-1">
                  <h3 className="text-sm font-semibold text-gray-700 dark:text-slate-200 mb-2">
                    Read Status
                  </h3>
                  <div className="space-y-1">
                    <label className="flex items-center h-8 space-x-2 cursor-pointer group px-2 rounded-lg hover:bg-gray-50 dark:hover:bg-slate-700">
                      <input
                        type="checkbox"
                        checked={readStatusFilters.read}
                        onChange={(e) =>
                          setReadStatusFilters((prev) => ({
                            ...prev,
                            read: e.target.checked,
                          }))
                        }
                        className="w-4 h-4 text-blue-600 border-gray-300 dark:border-slate-600 rounded focus:ring-blue-500 bg-white dark:bg-slate-900"
                      />
                      <Eye className="w-4 h-4 text-gray-400 dark:text-slate-500" />
                      <span className="text-sm text-gray-700 dark:text-slate-300 group-hover:text-gray-900 dark:group-hover:text-white transition-colors">Read</span>
                      {readStatusFilters.read && (
                        <span className="text-xs text-gray-500 dark:text-slate-500 ml-auto">
                          ({getReadCount()})
                        </span>
                      )}
                    </label>
                    <label className="flex items-center h-8 space-x-2 cursor-pointer group px-2 rounded-lg hover:bg-gray-50 dark:hover:bg-slate-700">
                      <input
                        type="checkbox"
                        checked={readStatusFilters.unread}
                        onChange={(e) =>
                          setReadStatusFilters((prev) => ({
                            ...prev,
                            unread: e.target.checked,
                          }))
                        }
                        className="w-4 h-4 text-blue-600 border-gray-300 dark:border-slate-600 rounded focus:ring-blue-500 bg-white dark:bg-slate-900"
                      />
                      <EyeOff className="w-4 h-4 text-gray-400 dark:text-slate-500" />
                      <span className="text-sm text-gray-700 dark:text-slate-300 group-hover:text-gray-900 dark:group-hover:text-white transition-colors">Unread</span>
                      {readStatusFilters.unread && (
                        <span className="text-xs text-gray-500 dark:text-slate-500 ml-auto">
                          ({getUnreadCount()})
                        </span>
                      )}
                    </label>
                  </div>
                </div>
              </div>

              
            </div>

            {/* Footer */}
            <div className="px-4 py-3 border-t border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-900/50 rounded-b-xl transition-colors duration-200 flex items-center justify-between">
              <button
                onClick={clearAllFilters}
                disabled={!hasActiveFilters()}
                className={`h-8 flex items-center justify-center text-xs font-medium transition-colors px-3 rounded-lg ${
                  hasActiveFilters()
                    ? "text-gray-700 dark:text-slate-200 hover:text-gray-900 dark:hover:text-white"
                    : "text-gray-300 dark:text-slate-600 cursor-not-allowed"
                }`}
              >
                Clear all
              </button>

              <span className="text-xs text-gray-400 dark:text-slate-500">
                {filteredCount} of {cards.length} cards
              </span>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default FilterPanel;
